import { useMemo } from "react";
import {
  useAdminAttendance,
  useAdminLeave,
  useAdminLeaveSummary,
  useAdminUsers,
  useHolidays,
} from "@/api/queries";
import { PageHeader } from "@/app/page-header";
import {
  CategoryBarChart,
  HeroFigure,
  StackedBarChart,
  StatTile,
  TrendChart,
} from "@/components/charts";
import { STATUS_SERIES_COLOR } from "@/components/charts/stacked-bar-chart";
import { Icon } from "@/components/ui/icon";
import { Panel, PanelHeader } from "@/components/ui/panel";
import {
  attendanceRate,
  countBy,
  leaveDaysByMonth,
  tallyByDay,
  tallyByTeam,
  tenureDistribution,
} from "@/lib/analytics";
import { holidayMap, leaveMap, shiftDate, todayStr } from "@/lib/calendar";
import { orgYear } from "@/lib/org-time";
import { formatDate } from "@/lib/format";
import { EMPLOYMENT_TYPE_LABEL, WORK_MODE_LABEL } from "@/lib/labels";

/** Rolling 30 days, matching how the rest of the app frames "recent". */
function analyticsRange() {
  const end = todayStr();
  return { start: shiftDate(end, -29), end };
}

export function HrAnalyticsPage() {
  const { start, end } = useMemo(analyticsRange, []);
  const users = useAdminUsers();
  const attendance = useAdminAttendance(start, end);
  const leave = useAdminLeave();
  const summary = useAdminLeaveSummary();
  const holidays = useHolidays();

  // HR & Admin are employees of the company too, so they belong in headcount,
  // analytics and the directory alongside everyone else.
  const staff = users.data ?? [];
  const holidayNames = useMemo(() => holidayMap(holidays.data), [holidays.data]);

  // Approved leave for the same range, so a sanctioned absence is out of the
  // denominator rather than counted as a missed day. See the same map on the HR
  // Attendance page — these two screens now classify a day identically.
  const leaveDaysByPerson = useMemo(() => leaveMap(attendance.data?.leave), [attendance.data?.leave]);

  const dayTallies = useMemo(
    () =>
      attendance.data
        ? tallyByDay(
            attendance.data.users,
            attendance.data.records,
            start,
            end,
            holidayNames,
            leaveDaysByPerson,
          )
        : [],
    [attendance.data, start, end, holidayNames, leaveDaysByPerson],
  );

  const teamTallies = useMemo(
    () =>
      attendance.data
        ? tallyByTeam(
            attendance.data.users,
            attendance.data.records,
            start,
            end,
            holidayNames,
            leaveDaysByPerson,
          )
        : [],
    [attendance.data, start, end, holidayNames, leaveDaysByPerson],
  );

  const rate = attendanceRate(dayTallies);
  const byTeam = useMemo(() => countBy(staff, (u) => u.team_name), [staff]);
  const byWorkMode = useMemo(
    () => countBy(staff, (u) => WORK_MODE_LABEL[u.work_mode]),
    [staff],
  );
  const byEmployment = useMemo(
    () => countBy(staff, (u) => EMPLOYMENT_TYPE_LABEL[u.employment_type]),
    [staff],
  );
  const byRole = useMemo(
    () => countBy(staff, (u) => (u.role === "manager" ? "Manager" : "Employee")),
    [staff],
  );
  const tenure = useMemo(() => tenureDistribution(staff), [staff]);
  const leaveMonths = useMemo(() => leaveDaysByMonth(leave.data ?? []), [leave.data]);

  /** Joiners per month, from real joining dates only. */
  const joinersByMonth = useMemo(() => {
    const months = new Map<string, number>();
    for (const person of staff) {
      if (!person.date_joined) continue;
      const key = person.date_joined.slice(0, 7);
      months.set(key, (months.get(key) ?? 0) + 1);
    }
    return [...months.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-12)
      .map(([month, joiners]) => ({
        month: formatDate(`${month}-01`, "MMM yy") ?? month,
        joiners,
      }));
  }, [staff]);

  const trendData = dayTallies.map((tally) => ({
    day: formatDate(tally.date, "d MMM") ?? tally.date,
    complete: tally.complete,
    half: tally.half,
    absent: tally.absent,
  }));

  const missingJoinDate = staff.filter((person) => !person.date_joined).length;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Admin Account only"
        title="Analytics"
        sub="Workforce reporting built from the records this system actually holds. Every figure below is computed from live accounts, attendance and leave, so an empty chart means there is genuinely nothing recorded yet."
      />

      <Panel className="p-5">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <HeroFigure
            value={staff.length}
            label="Total headcount"
            loading={users.isLoading}
            meta={
              <span>
                Across {byTeam.length} {byTeam.length === 1 ? "team" : "teams"} · everyone on the
                system, HR and Admin included
              </span>
            }
          />
          <div className="grid flex-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <StatTile
              label="Full-day rate, last 30 days"
              value={rate === null ? "—" : `${rate}%`}
              icon="attendance"
              loading={attendance.isLoading}
              meta={rate === null ? "No attendance recorded" : "of working-day slots"}
            />
            <StatTile
              label="Managers"
              value={staff.filter((u) => u.role === "manager").length}
              icon="user-accounts"
              loading={users.isLoading}
              meta={`${staff.filter((u) => u.role === "employee").length} employees`}
            />
            <StatTile
              label="Leave requests logged"
              value={(leave.data ?? []).length}
              icon="leave"
              loading={leave.isLoading}
              meta="All statuses, all time"
            />
          </div>
        </div>
      </Panel>

      <div className="grid gap-4 xl:grid-cols-2">
        <CategoryBarChart
          title="Headcount by team"
          note="Every account on the system, by the team it is mapped to"
          data={byTeam}
          valueHeader="People"
          loading={users.isLoading}
          {...(users.error ? { error: users.error.message } : {})}
          emptyBody="No accounts have been created yet."
        />

        <CategoryBarChart
          title="Tenure spread"
          note="Whole months since each person's recorded joining date"
          data={tenure}
          valueHeader="People"
          orientation="vertical"
          loading={users.isLoading}
          emptyBody={
            missingJoinDate > 0
              ? "Joining dates are entered by each person during account setup."
              : "No accounts have been created yet."
          }
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <CategoryBarChart
          title="Work mode"
          note="How each account is configured"
          data={byWorkMode}
          valueHeader="People"
          loading={users.isLoading}
          height={160}
          emptyBody="No accounts yet."
        />
        <CategoryBarChart
          title="Employment type"
          note="Drives real leave-policy eligibility"
          data={byEmployment}
          valueHeader="People"
          loading={users.isLoading}
          height={160}
          emptyBody="No accounts yet."
        />
        <CategoryBarChart
          title="Role split"
          note="Manager accounts against employee accounts"
          data={byRole}
          valueHeader="People"
          loading={users.isLoading}
          height={160}
          emptyBody="No accounts yet."
        />
      </div>

      <TrendChart
        title="Attendance over the last 30 days"
        note="Working days only. Weekly offs and company holidays are excluded, not counted as absence."
        data={trendData}
        xKey="day"
        series={[
          { key: "complete", label: "Full day", color: STATUS_SERIES_COLOR.complete },
          { key: "half", label: "Half day", color: STATUS_SERIES_COLOR.half },
          { key: "absent", label: "No record", color: STATUS_SERIES_COLOR.absent },
        ]}
        loading={attendance.isLoading}
        {...(attendance.error ? { error: attendance.error.message } : {})}
        emptyBody="No attendance has been recorded in the last 30 days."
      />

      <div className="grid gap-4 xl:grid-cols-2">
        <StackedBarChart
          title="Attendance quality by team"
          note="Person-days over the last 30 working days, as a share of each team's total"
          data={teamTallies as unknown as Record<string, string | number>[]}
          categoryKey="team"
          orientation="horizontal"
          percentage
          series={[
            { key: "complete", label: "Full day", color: STATUS_SERIES_COLOR.complete },
            { key: "half", label: "Half day", color: STATUS_SERIES_COLOR.half },
            { key: "absent", label: "No record", color: STATUS_SERIES_COLOR.absent },
          ]}
          loading={attendance.isLoading}
          height={Math.max(200, teamTallies.length * 44)}
          emptyBody="No attendance recorded for any team."
        />

        <TrendChart
          title="Approved leave days by month"
          note={`Calendar year ${summary.data?.year ?? orgYear()}`}
          data={leaveMonths}
          xKey="month"
          series={[{ key: "days", label: "Days approved" }]}
          area
          loading={leave.isLoading}
          emptyBody="No leave has been approved this year yet."
        />
      </div>

      <TrendChart
        title="Joiners by month"
        note="From the joining date each person entered during account setup, last 12 months with activity"
        data={joinersByMonth}
        xKey="month"
        series={[{ key: "joiners", label: "People joined" }]}
        loading={users.isLoading}
        emptyBody="No joining dates have been recorded yet."
      />

      <Panel>
        <PanelHeader title="What this page cannot show yet" />
        <div className="px-4 py-4">
          <p className="max-w-[65ch] text-sm leading-relaxed text-muted">
            Attrition, exit reasons and cost-per-hire are missing from this page on purpose. The
            system has no offboarding records, no exit dates and no recruitment data, so any
            attrition figure would have to be invented rather than measured.
          </p>
          <ul className="mt-3 flex flex-col gap-1.5">
            {[
              "Attrition and retention need an exit date and reason per person",
              "Cost and time to hire need the recruitment module",
              "Performance distribution needs the KRA/KPI module",
            ].map((item) => (
              <li key={item} className="flex items-start gap-2 text-[13px] leading-relaxed text-ink-soft">
                <Icon name="minus" size={16} className="mt-0.5 text-faint" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </Panel>
    </div>
  );
}
